import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowUpCircle,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  Filter,
  Search,
  Eye,
  X,
  SlidersHorizontal,
} from "lucide-react";
import ProductCard from "../components/ProductCard";
import { motion, AnimatePresence } from "framer-motion";
import AuthenticatedNavbar from "../components/AuthenticatedNavbar";
import TopNavbar from "../components/TopNavbar";
import Footer from "../components/Landing/Footer";
import HeroCarousel from "../components/Landing/HeroCarousel";
import { useSelector } from "react-redux";
import { categoryOptions } from "../data/categories";
import { regionOptions, locationsByRegion } from "../data/regions";
import dealsService from "../components/services/DealsService";
import { toast } from "react-toastify";

const ProductCardSkeleton = () => (
  <div className="flex overflow-hidden flex-col h-full bg-white rounded-xl border border-gray-100 shadow-sm animate-pulse">
    <div className="relative pt-[60%] bg-gray-200">
      {/* Featured badge skeleton */}
      <div className="absolute top-2 left-2 z-10 w-16 h-5 bg-gray-300 rounded-md"></div>

      {/* Discount skeleton */}
      <div className="absolute top-2 right-2 z-10 w-12 h-8 bg-gray-300 rounded-md"></div>

      {/* Commission badge skeleton */}
      <div className="absolute right-3 bottom-3 w-20 h-6 bg-gray-300 rounded-full"></div>
    </div>

    <div className="flex flex-col flex-1 p-3">
      {/* Company name skeleton */}
      <div className="mb-2 w-24 h-4 bg-gray-200 rounded"></div>

      {/* Title skeleton */}
      <div className="mb-2 w-full h-5 bg-gray-200 rounded"></div>
      <div className="mb-3 w-3/4 h-5 bg-gray-200 rounded"></div>

      {/* Price skeleton */}
      <div className="flex gap-2 items-center mb-3">
        <div className="w-20 h-6 bg-gray-200 rounded"></div>
        <div className="w-16 h-4 bg-gray-200 rounded"></div>
      </div>

      {/* Action buttons skeleton */}
      <div className="flex gap-1">
        <div className="flex-1 h-8 bg-gray-300 rounded-lg"></div>
        <div className="w-16 h-8 bg-gray-300 rounded-lg"></div>
      </div>
    </div>
  </div>
);

const TrendingDeals = ({ isAuthenticated = false }) => {
  const { user, isAuthenticated: authState } = useSelector(
    (state) => state.auth
  );

  const isUserAuthenticated = authState || isAuthenticated;
  const [deals, setDeals] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);
  const [sortOption, setSortOption] = useState("trending");
  const [viewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const resultsContainerRef = useRef(null);
  const resultsGridRef = useRef(null);

  // Advanced search state
  const [advancedSearch, setAdvancedSearch] = useState({
    searchTerm: "",
    category: "",
    region: "",
    city: "",
  });

  // Update available cities when region changes
  const [availableCities, setAvailableCities] = useState([]);

  useEffect(() => {
    if (advancedSearch.region) {
      const cities = locationsByRegion[advancedSearch.region] || [];
      const transformedCities = cities.map((city) => ({
        value: city,
        label: city,
      }));
      setAvailableCities(transformedCities);
    } else {
      setAvailableCities([]);
    }
  }, [advancedSearch.region]);

  // Build active filters array for display
  const activeFilters = [];
  if (advancedSearch.category)
    activeFilters.push({ name: "category", value: advancedSearch.category });
  if (advancedSearch.region)
    activeFilters.push({ name: "region", value: advancedSearch.region });
  if (advancedSearch.city)
    activeFilters.push({ name: "city", value: advancedSearch.city });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const fetchDeals = async () => {
      try {
        const response = await dealsService.getAllDeals();
        console.log("response", response?.data?.data);
        if (response?.data?.status_code === "000") {
          const dealsData = response?.data?.data;
          let dealsArray = [];

          if (Array.isArray(dealsData)) {
            // Already an array
            dealsArray = dealsData;
          } else if (dealsData && typeof dealsData === "object") {
            // Check if the object has a property that contains an array of deals
            const possibleArrayKeys = ["deals", "items", "data", "results"];
            let foundArray = null;

            for (const key of possibleArrayKeys) {
              if (dealsData[key] && Array.isArray(dealsData[key])) {
                foundArray = dealsData[key];
                break;
              }
            }

            if (foundArray) {
              dealsArray = foundArray;
            } else {
              // If no array found, convert the object values to array
              dealsArray = Object.values(dealsData).filter(
                (item) => item && typeof item === "object" && item.id
              );
            }
          } else {
            // No data or unexpected type
            dealsArray = [];
          }
          const transformedDeals = dealsArray.map((deal) => {
            // Parse partner_location if it's a string
            let parsedPartnerLocation = null;
            if (deal.partner_location && typeof deal.partner_location === 'string') {
              try {
                parsedPartnerLocation = JSON.parse(deal.partner_location);
              } catch (e) {
                console.error('Error parsing partner_location:', e);
                parsedPartnerLocation = [];
              }
            } else if (Array.isArray(deal.partner_location)) {
              parsedPartnerLocation = deal.partner_location;
            }

            return {
              id: deal.id,
              title: deal.title,
              company_name: deal.company_name,
              category: Array.isArray(deal.category)
                ? deal.category[0]
                : deal.category,
              region: deal.region_location?.[0]?.region || deal.redemption_region_location?.[0]?.region || "all",
              region_location: deal.region_location?.[0]?.location || deal.redemption_region_location?.[0]?.location || "all",
              partner_location: parsedPartnerLocation,
              redemption_partners: deal.redemption_partners || [],
              target_branches: deal.target_branches || [],
              redemption_branches: deal.redemption_branches || [],
              image: deal.image,
              company_logo: deal.company_logo,
              original_price: deal.price,
              price: deal.price,
              discounted_price: deal.discounted_price,
              originalPrice: deal.price,
              discountedPrice: deal.discounted_price,
              discount_percentage: deal.discount_percentage,
              commission: deal.promote_deal ? "5%" : "0%",
              trendingScore: deal.subscriber_count || 0,
              expiry_date: deal.expiry_date,
              expiryDate: deal.expiry_date,
              start_date: deal.start_date,
              startDate: deal.start_date,
              status: deal.status,
              deal_slug: deal.deal_slug,
              vendor_id: deal.vendor_id,
              terms: deal.terms,
              refund_policy: deal.refund_policy,
              redemption_type: deal.redemption_type,
              details: deal.details,
              contact: deal.contact,
              google_map_url: deal.google_map_url,
              badge: deal.status === "Approved" ? "Featured" : null,
              validity_type: deal.validity_type,
              flash: deal.flash || [],
              offer_type: deal.offer_type,
              bundle: deal.bundle || [],
              bogo: deal.bogo || [],
            };
          });
          setAllDeals(transformedDeals);
          setDeals(transformedDeals);
          setTotalResults(transformedDeals.length);
          setTotalPages(Math.ceil(transformedDeals.length / itemsPerPage));
        } else {
          console.log("response", response);
          toast.error(response?.data?.reason || "Something went wrong");
        }
      } catch (error) {
        console.log("error", error);
        toast.error(error?.response?.data?.reason || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, [itemsPerPage]);

  // Function to fetch trending deals
  const fetchTrendingDeals = useCallback(async () => {
    if (allDeals.length === 0) return;
    setFadeIn(false);

    setTimeout(() => {
      setLoading(true);
      setError(null);

      try {
        setTimeout(() => {
          let filteredDeals = [...allDeals];

          // Apply search filter
          if (searchTerm || advancedSearch.searchTerm) {
            const searchValue = searchTerm || advancedSearch.searchTerm;
            filteredDeals = filteredDeals.filter((deal) => {
              // Check string fields
              const matchesBasicFields = 
                deal.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
                deal.company_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                deal.category?.toLowerCase().includes(searchValue.toLowerCase()) ||
                deal.region?.toLowerCase().includes(searchValue.toLowerCase()) ||
                deal.region_location?.toLowerCase().includes(searchValue.toLowerCase());
              
              // Check partner_location if it's an array
              const matchesPartnerLocation = Array.isArray(deal.partner_location) 
                ? deal.partner_location.some(partner => 
                    JSON.stringify(partner).toLowerCase().includes(searchValue.toLowerCase())
                  )
                : false;
              
              // Check redemption_partners
              const matchesRedemptionPartners = Array.isArray(deal.redemption_partners)
                ? deal.redemption_partners.some(partner => 
                    partner.partner_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    partner.branches?.some(branch => 
                      branch.location?.toLowerCase().includes(searchValue.toLowerCase())
                    )
                  )
                : false;
              
              return matchesBasicFields || matchesPartnerLocation || matchesRedemptionPartners;
            });
          }

          // Apply category filter
          if (advancedSearch.category) {
            filteredDeals = filteredDeals.filter((deal) =>
              deal.category
                .toLowerCase()
                .includes(advancedSearch.category.toLowerCase())
            );
          }

          // Apply region filter
          if (advancedSearch.region) {
            filteredDeals = filteredDeals.filter((deal) =>
              deal.region
                .toLowerCase()
                .includes(advancedSearch.region.toLowerCase())
            );
          }

          // Apply city filter
          if (advancedSearch.city) {
            filteredDeals = filteredDeals.filter((deal) =>
              deal.region_location
                .toLowerCase()
                .includes(advancedSearch.city.toLowerCase())
            );
          }

          // Apply sorting
          switch (sortOption) {
            case "trending":
              filteredDeals.sort((a, b) => b.trendingScore - a.trendingScore);
              break;
            case "newest":
              filteredDeals.sort(
                (a, b) => new Date(b.startDate) - new Date(a.startDate)
              );
              break;
            case "oldest":
              filteredDeals.sort(
                (a, b) => new Date(a.startDate) - new Date(b.startDate)
              );
              break;
            case "discount_high":
              filteredDeals.sort((a, b) => {
                const discountA =
                  ((a.originalPrice - a.discountedPrice) / a.originalPrice) *
                  100;
                const discountB =
                  ((b.originalPrice - b.discountedPrice) / b.originalPrice) *
                  100;
                return discountB - discountA;
              });
              break;
            case "commission_high":
              filteredDeals.sort(
                (a, b) => parseFloat(b.commission) - parseFloat(a.commission)
              );
              break;
            default:
              break;
          }

          // Apply pagination
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          const paginatedDeals = filteredDeals.slice(startIndex, endIndex);

          setDeals(paginatedDeals);
          setTotalResults(filteredDeals.length);
          setTotalPages(Math.ceil(filteredDeals.length / itemsPerPage));
          setLoading(false);
          setFadeIn(true);
        }, 800);
      } catch (err) {
        console.error("Error fetching trending deals:", err);
        setError("Failed to load trending deals. Please try again.");
        setLoading(false);
        setFadeIn(true);
      }
    }, 300);
  }, [
    allDeals,
    searchTerm,
    advancedSearch,
    sortOption,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (allDeals.length > 0) {
      fetchTrendingDeals();
      // Always scroll to top when component renders
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [fetchTrendingDeals, allDeals.length]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        searchTerm !== "" ||
        Object.values(advancedSearch).some((value) => value !== "")
      ) {
        fetchTrendingDeals();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, advancedSearch, fetchTrendingDeals]);

  // Handle scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchTrendingDeals();
  };

  // Handle clearing specific filter
  const handleClearFilter = (filterName) => {
    if (filterName === "searchTerm") {
      setAdvancedSearch((prev) => ({ ...prev, searchTerm: "" }));
      setSearchTerm("");
    } else {
      setAdvancedSearch((prev) => {
        const updated = { ...prev, [filterName]: "" };

        if (filterName === "region") {
          updated.city = "";
        }

        return updated;
      });
    }
    setCurrentPage(1);
  };

  // Clear all filters
  const handleClearAllFilters = (e) => {
    if (e) e.preventDefault();

    setSearchTerm("");
    setAdvancedSearch({
      searchTerm: "",
      category: "",
      region: "",
      city: "",
    });
    setCurrentPage(1);
  };

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (showAdvancedFilter) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAdvancedFilter]);

  return (
    <>
      {isUserAuthenticated ? <AuthenticatedNavbar /> : <TopNavbar />}

      {/* Hero Carousel */}
      <HeroCarousel />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
        {/* Header */}

        <div
          className="px-4 py-16 mx-auto max-w-8xl sm:px-6 lg:px-8"
          ref={resultsContainerRef}
        >
          {/* Compact Filter Bar */}
          <div className="overflow-hidden mb-6 bg-white rounded-xl border border-gray-100 shadow-md">
            <div className="flex flex-col items-center p-3 border-b border-gray-100 sm:flex-row">
              <form
                onSubmit={handleSearch}
                className="relative w-full sm:max-w-md"
              >
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <Search className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search trending deals..."
                  className="pl-10 pr-20 py-2 w-full text-gray-700 bg-gray-50 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF7F7F] focus:border-[#FF7F7F]"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="absolute right-1 top-1 bottom-1 px-3 text-xs font-medium text-white bg-gradient-to-r from-[#FF7F7F] to-pink-600 rounded transition-all disabled:opacity-70"
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </form>

              <motion.button
                onClick={() => setShowAdvancedFilter(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex gap-2 items-center px-4 py-2 mt-3 text-sm font-medium text-gray-700 bg-gray-50 rounded-md border border-gray-200 transition-all sm:mt-0 sm:ml-3 hover:bg-gray-100"
              >
                <SlidersHorizontal className="w-4 h-4 text-[#FF7F7F]" />
                Advanced Filters
              </motion.button>
            </div>

            {(activeFilters.length > 0 || searchTerm) && (
              <div className="px-4 py-3 bg-gray-50">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="mr-1 text-xs font-medium text-gray-500">
                    Active Filters:
                  </span>

                  {/* Search Term Filter */}
                  {searchTerm && (
                    <div className="flex items-center px-2 py-1 bg-white rounded-full border border-[#FFCACA] shadow-sm">
                      <span className="mr-1 text-xs font-medium text-[#FF7F7F]">
                        Search:
                      </span>
                      <span className="text-xs text-gray-700 max-w-[120px] truncate">
                        {searchTerm}
                      </span>
                      <button
                        onClick={() => handleClearFilter("searchTerm")}
                        className="ml-1 p-0.5 text-gray-400 hover:text-[#FF7F7F] transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}

                  {activeFilters.map((filter, index) => (
                    <div
                      key={index}
                      className="flex items-center px-2 py-1 bg-white rounded-full border border-[#FFCACA] shadow-sm"
                    >
                      <span className="mr-1 text-xs font-medium text-[#FF7F7F]">
                        {filter.name.charAt(0).toUpperCase() +
                          filter.name.slice(1)}
                        :
                      </span>
                      <span className="text-xs text-gray-700 max-w-[100px] truncate">
                        {filter.value}
                      </span>
                      <button
                        onClick={() => handleClearFilter(filter.name)}
                        className="ml-1 p-0.5 text-gray-400 hover:text-[#FF7F7F] transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={handleClearAllFilters}
                    className="text-xs text-[#FF7F7F] hover:underline font-medium"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results Header */}
          <div className="flex flex-col gap-2 mb-6 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex gap-2 items-center">
              <TrendingUp className="w-5 h-5 text-[#FF7F7F]" />
              <h2 className="text-lg font-semibold text-gray-900">
                {totalResults} Trending Deals Found
              </h2>
            </div>
            {!isUserAuthenticated && (
              <div className="flex gap-2 items-center px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <Eye className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  Login to access full features and start promoting
                </span>
              </div>
            )}
          </div>

          <div
            ref={resultsGridRef}
            className={`transition-opacity duration-300 ease-in-out ${
              fadeIn ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Loading state */}
            {loading && (
              <div
                className={`grid gap-4 sm:gap-6 ${
                  viewMode === "grid"
                    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    : "grid-cols-1"
                }`}
              >
                {Array.from({ length: itemsPerPage }).map((_, idx) => (
                  <ProductCardSkeleton key={idx} />
                ))}
              </div>
            )}

            {/* Error state */}
            {error && !loading && deals.length === 0 && (
              <div className="flex flex-col justify-center items-center p-8 mb-6 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="flex justify-center items-center mb-4 w-16 h-16 bg-red-50 rounded-full">
                  <AlertCircle size={24} className="text-red-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-800">
                  Something went wrong
                </h3>
                <p className="mb-4 max-w-md text-gray-600">{error}</p>
                <motion.button
                  onClick={fetchTrendingDeals}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center px-4 py-2 text-gray-800 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Try Again
                </motion.button>
              </div>
            )}

            {/* No results state */}
            {!loading &&
              !error &&
              deals.length === 0 &&
              allDeals.length > 0 && (
                <div className="flex flex-col justify-center items-center p-8 mb-6 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
                  <div className="flex justify-center items-center mb-4 w-16 h-16 bg-gray-50 rounded-full">
                    <Filter size={24} className="text-gray-500" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-gray-800">
                    No trending deals found
                  </h3>
                  <p className="mb-4 max-w-md text-gray-600">
                    We couldn't find any trending deals matching your search
                    criteria.
                  </p>
                  <motion.button
                    onClick={() => {
                      setSearchTerm("");
                      setSortOption("trending");
                      setCurrentPage(1);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-lg shadow-sm hover:shadow transition-all"
                  >
                    Clear Filters
                  </motion.button>
                </div>
              )}

            {/* Results grid */}
            {!loading && deals.length > 0 && (
              <>
                <div
                  className={`grid gap-4 sm:gap-6 ${
                    viewMode === "grid"
                      ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                      : "grid-cols-1"
                  }`}
                >
                  {deals.map((deal, index) => (
                    <motion.div
                      key={deal.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { delay: index * 0.05, duration: 0.3 },
                      }}
                    >
                      <ProductCard
                        deal={deal}
                        index={index}
                        isAuthenticated={isUserAuthenticated}
                      />
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col gap-2 justify-center items-center mt-8 sm:flex-row sm:gap-4">
                    <div className="flex items-center text-sm text-gray-700">
                      <span className="font-medium">{currentPage}</span> of{" "}
                      <span className="font-medium">{totalPages}</span> Pages
                    </div>

                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg border ${
                          currentPage === 1
                            ? "text-gray-400 border-gray-200 cursor-not-allowed"
                            : "text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {/* Page numbers */}
                      <div className="hidden gap-1 items-center sm:flex">
                        {/* First page */}
                        {currentPage > 2 && (
                          <button
                            onClick={() => handlePageChange(1)}
                            className={`flex items-center justify-center w-8 h-8 text-sm rounded-lg ${
                              currentPage === 1
                                ? "bg-[#FF6C6C] text-white"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            1
                          </button>
                        )}

                        {/* Ellipsis for many pages */}
                        {currentPage > 3 && (
                          <span className="px-1 text-gray-500">...</span>
                        )}

                        {/* Page before current */}
                        {currentPage > 1 && (
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="flex justify-center items-center w-8 h-8 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
                          >
                            {currentPage - 1}
                          </button>
                        )}

                        {/* Current page */}
                        <button className="flex items-center justify-center w-8 h-8 text-sm text-white bg-[#FF6C6C] rounded-lg">
                          {currentPage}
                        </button>

                        {/* Page after current */}
                        {currentPage < totalPages && (
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="flex justify-center items-center w-8 h-8 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
                          >
                            {currentPage + 1}
                          </button>
                        )}

                        {/* Ellipsis for many pages */}
                        {currentPage < totalPages - 2 && (
                          <span className="px-1 text-gray-500">...</span>
                        )}

                        {/* Last page */}
                        {currentPage < totalPages - 1 && (
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            className={`flex items-center justify-center w-8 h-8 text-sm rounded-lg ${
                              currentPage === totalPages
                                ? "bg-[#FF6C6C] text-white"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {totalPages}
                          </button>
                        )}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg border ${
                          currentPage === totalPages
                            ? "text-gray-400 border-gray-200 cursor-not-allowed"
                            : "text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Scroll to top button */}
          <AnimatePresence>
            {showScrollToTop && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={scrollToTop}
                className="fixed z-50 p-2 bg-[#FF6C6C] rounded-full shadow-lg bottom-6 right-6 md:bottom-8 md:right-8 text-white transition-all hover:bg-[#FF8F8F]"
                aria-label="Scroll to top"
              >
                <ArrowUpCircle size={24} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Advanced Filter Sidebar */}
      <AnimatePresence>
        {showAdvancedFilter && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAdvancedFilter(false)}
              className="fixed inset-0 z-40 bg-black bg-opacity-50"
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="flex fixed top-0 left-0 z-50 flex-col w-full max-w-sm h-full bg-white shadow-2xl"
            >
              {/* Sidebar Header */}
              <div className="flex flex-shrink-0 justify-between items-center px-6 py-4 bg-gradient-to-r from-gray-50 border-b border-gray-100 to-blue-50/30">
                <div className="flex gap-2 items-center">
                  <SlidersHorizontal className="w-5 h-5 text-[#FF7F7F]" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Advanced Filters
                  </h3>
                </div>
                <button
                  onClick={() => setShowAdvancedFilter(false)}
                  className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Sidebar Content */}
              <div className="overflow-y-auto flex-1">
                <div className="p-6 space-y-6">
                  {/* Sort Options */}
                  <div>
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      Sort Results
                    </label>
                    <select
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent transition-all duration-200 bg-gray-50/50"
                    >
                      <option value="trending">
                        🔥 Trending (Most Popular)
                      </option>
                      <option value="newest">🆕 Newest First</option>
                      <option value="oldest">📅 Oldest First</option>
                      <option value="discount_high">💰 Highest Discount</option>
                      <option value="commission_high">
                        💎 Highest Commission
                      </option>
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      Category
                    </label>
                    <select
                      value={advancedSearch.category}
                      onChange={(e) =>
                        setAdvancedSearch((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent transition-all duration-200 bg-gray-50/50"
                    >
                      <option value="">All Categories</option>
                      {categoryOptions.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Region Filter */}
                  <div>
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      Region
                    </label>
                    <select
                      value={advancedSearch.region}
                      onChange={(e) =>
                        setAdvancedSearch((prev) => ({
                          ...prev,
                          region: e.target.value,
                          city: "",
                        }))
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent transition-all duration-200 bg-gray-50/50"
                    >
                      <option value="">All Regions</option>
                      {regionOptions.map((region) => (
                        <option key={region.value} value={region.value}>
                          {region.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* City Filter */}
                  <div>
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      City
                    </label>
                    <select
                      value={advancedSearch.city}
                      onChange={(e) =>
                        setAdvancedSearch((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      disabled={!advancedSearch.region}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent transition-all duration-200 bg-gray-50/50 disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="">
                        {advancedSearch.region
                          ? "All Cities"
                          : "Select Region First"}
                      </option>
                      {availableCities.map((city) => (
                        <option key={city.value} value={city.value}>
                          {city.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Additional Filter Options */}
                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="mb-3 text-sm font-semibold text-gray-700">
                      Quick Actions
                    </h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          setSortOption("trending");
                          setAdvancedSearch({
                            searchTerm: "",
                            category: "",
                            region: "",
                            city: "",
                          });
                        }}
                        className="px-4 py-2 w-full text-sm text-left text-gray-700 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                      >
                        🔥 Show Trending Only
                      </button>
                      <button
                        onClick={() => {
                          setSortOption("discount_high");
                          setAdvancedSearch({
                            searchTerm: "",
                            category: "",
                            region: "",
                            city: "",
                          });
                        }}
                        className="px-4 py-2 w-full text-sm text-left text-gray-700 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                      >
                        💰 Highest Discounts
                      </button>
                      <button
                        onClick={() => {
                          setSortOption("commission_high");
                          setAdvancedSearch({
                            searchTerm: "",
                            category: "",
                            region: "",
                            city: "",
                          });
                        }}
                        className="px-4 py-2 w-full text-sm text-left text-gray-700 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                      >
                        💎 Highest Commission
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sidebar Actions */}
                <div className="flex-shrink-0 p-6 bg-white border-t border-gray-100">
                  <div className="flex gap-3">
                    <button
                      onClick={handleClearAllFilters}
                      className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl transition-all duration-200 hover:bg-gray-200"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => setShowAdvancedFilter(false)}
                      className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#FF7F7F] to-pink-600 rounded-xl transition-all duration-200 hover:from-[#ff6b6b] hover:to-pink-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {!user && <Footer />}
    </>
  );
};

export default TrendingDeals;
